import { Button } from '@/global/components/Button'
import { cn } from '@/lib/utils'
import React, { useEffect, useRef, useState } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface IProps {
    name: string
    passos: IPasso[]
}

export default function OverlayApresentacao({ name, passos }: IProps) {
    const useStore = makeStore(name, passos)
    const {
        aberto,
        abrir,
        anterior,
        proximo,
        ignorar,
        isTudoCompleto,
        passoAtual,
    } = useStore()

    useEffect(() => {
        if (
            !isTudoCompleto &&
            aberto == null &&
            !document
                .getElementById('overlay-apresentacao')
                ?.checkVisibility({ opacityProperty: true })
        )
            abrir()
    }, [])

    const zero = {
        width: 0,
        height: 0,
        top: window.innerHeight / 2,
        left: window.innerWidth / 2,
    }

    const [dimensoes, setDimensoes] = useState({
        ...zero,
        calculando: false,
    })
    const calculoAtualRef = useRef(0)
    const popupRef = useRef<HTMLDivElement>(null)
    const [posicaoPopup, setPosicaoPopup] = useState({
        top: window.innerHeight / 2,
        left: window.innerWidth / 2,
        pronto: false,
    })

    useEffect(() => {
        const execucaoId = ++calculoAtualRef.current
        const isExecucaoAtual = () => execucaoId === calculoAtualRef.current

        const sleep = (ms: number) =>
            new Promise(resolve => {
                setTimeout(resolve, ms)
            })

        function isElementoVisivel(elemento?: HTMLElement | null) {
            if (!elemento) return false

            const style = window.getComputedStyle(elemento)
            const rect = elemento.getBoundingClientRect()

            return (
                style.display !== 'none' &&
                style.visibility !== 'hidden' &&
                Number(style.opacity) > 0 &&
                rect.width > 0 &&
                rect.height > 0
            )
        }

        async function calcularDimensoes() {
            if (passoAtual === 0) await sleep(500)
            if (!isExecucaoAtual()) return

            const parentElements = passos.at(passoAtual)?.parentElements ?? []
            const getHighlightElement =
                passos.at(passoAtual)?.getHighlightElement

            if (!getHighlightElement) {
                if (!isExecucaoAtual()) return
                setDimensoes({ ...zero, calculando: false })
                return
            }

            if (!isExecucaoAtual()) return
            setDimensoes({ ...zero, calculando: true })

            for (let i = 0; i < parentElements.length; i++) {
                if (!isExecucaoAtual()) return

                const highlightAtual = getHighlightElement()
                if (isElementoVisivel(highlightAtual)) {
                    break
                }

                const el = parentElements[i]?.()
                const nextElement =
                    parentElements[i + 1]?.() ?? getHighlightElement?.()

                if (isElementoVisivel(nextElement)) {
                    console.log('Próximo elemento visível, pulando clique:', el)
                    continue
                }

                el?.click()
                await sleep(300)
            }

            if (!isExecucaoAtual()) return

            const elemento = getHighlightElement?.()
            if (!elemento) {
                if (!isExecucaoAtual()) return
                setDimensoes({ ...zero, calculando: false })
                return
            }

            elemento?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center',
            })
            await sleep(300)
            if (!isExecucaoAtual()) return

            const { width, height, top, left } =
                elemento?.getBoundingClientRect() ?? {}

            setDimensoes({
                width: width ?? 0,
                height: height ?? 0,
                top: top ?? 0,
                left: left ?? 0,
                calculando: false,
            })
        }

        if (passos.at(passoAtual) && !isTudoCompleto) calcularDimensoes()

        return () => {
            if (calculoAtualRef.current === execucaoId) {
                calculoAtualRef.current += 1
            }
        }
    }, [passoAtual, isTudoCompleto])

    useEffect(() => {
        if (!aberto) return

        const popup = popupRef.current
        if (!popup) return

        const margem = 16
        const larguraViewport = window.innerWidth
        const alturaViewport = window.innerHeight
        const larguraPopup = popup.offsetWidth
        const alturaPopup = popup.offsetHeight

        const clamp = (valor: number, min: number, max: number) =>
            Math.min(Math.max(valor, min), max)

        const converterOffset = (valor: string, total: number) => {
            const normalizado = valor.trim()

            if (normalizado.endsWith('%')) {
                const numero = Number.parseFloat(normalizado)
                if (!Number.isNaN(numero)) return (total * numero) / 100
            }

            const numero = Number.parseFloat(normalizado)
            return Number.isNaN(numero) ? 0 : numero
        }

        const centroBaseX =
            larguraViewport / 2 + converterOffset('0px', larguraViewport)
        const centroBaseY =
            alturaViewport / 2 + converterOffset('0px', alturaViewport)

        const normalizarPosicao = (novaPosicao: {
            top: number
            left: number
        }) => ({
            top: clamp(
                novaPosicao.top,
                margem,
                Math.max(margem, alturaViewport - alturaPopup - margem),
            ),
            left: clamp(
                novaPosicao.left,
                margem,
                Math.max(margem, larguraViewport - larguraPopup - margem),
            ),
        })

        const posicaoBase = normalizarPosicao({
            top: centroBaseY - alturaPopup / 2,
            left: centroBaseX - larguraPopup / 2,
        })

        const areaDestaque =
            dimensoes.width > 0 && dimensoes.height > 0
                ? {
                      top: dimensoes.top,
                      left: dimensoes.left,
                      right: dimensoes.left + dimensoes.width,
                      bottom: dimensoes.top + dimensoes.height,
                  }
                : null

        const sobrepoeDestaque = (posicao: { top: number; left: number }) => {
            if (!areaDestaque) return false

            const areaPopup = {
                top: posicao.top,
                left: posicao.left,
                right: posicao.left + larguraPopup,
                bottom: posicao.top + alturaPopup,
            }

            return !(
                areaPopup.right <= areaDestaque.left ||
                areaPopup.left >= areaDestaque.right ||
                areaPopup.bottom <= areaDestaque.top ||
                areaPopup.top >= areaDestaque.bottom
            )
        }

        const calcularAreaSobreposta = (posicao: {
            top: number
            left: number
        }) => {
            if (!areaDestaque) return 0

            const areaPopup = {
                top: posicao.top,
                left: posicao.left,
                right: posicao.left + larguraPopup,
                bottom: posicao.top + alturaPopup,
            }

            const larguraIntersecao = Math.max(
                0,
                Math.min(areaPopup.right, areaDestaque.right) -
                    Math.max(areaPopup.left, areaDestaque.left),
            )
            const alturaIntersecao = Math.max(
                0,
                Math.min(areaPopup.bottom, areaDestaque.bottom) -
                    Math.max(areaPopup.top, areaDestaque.top),
            )

            return larguraIntersecao * alturaIntersecao
        }

        const calcularDistanciaBase = (posicao: {
            top: number
            left: number
        }) =>
            Math.hypot(
                posicao.left - posicaoBase.left,
                posicao.top - posicaoBase.top,
            )

        let posicaoFinal = posicaoBase

        if (areaDestaque && sobrepoeDestaque(posicaoBase)) {
            const candidatas = [
                normalizarPosicao({
                    top: areaDestaque.bottom + margem,
                    left: centroBaseX - larguraPopup / 2,
                }),
                normalizarPosicao({
                    top: areaDestaque.top - alturaPopup - margem,
                    left: centroBaseX - larguraPopup / 2,
                }),
                normalizarPosicao({
                    top: centroBaseY - alturaPopup / 2,
                    left: areaDestaque.right + margem,
                }),
                normalizarPosicao({
                    top: centroBaseY - alturaPopup / 2,
                    left: areaDestaque.left - larguraPopup - margem,
                }),
            ]

            const candidatasSemSobreposicao = candidatas.filter(
                candidata => !sobrepoeDestaque(candidata),
            )

            if (candidatasSemSobreposicao.length > 0) {
                candidatasSemSobreposicao.sort(
                    (a, b) =>
                        calcularDistanciaBase(a) - calcularDistanciaBase(b),
                )
                posicaoFinal = candidatasSemSobreposicao[0]
            } else {
                candidatas.sort((a, b) => {
                    const areaA = calcularAreaSobreposta(a)
                    const areaB = calcularAreaSobreposta(b)
                    if (areaA !== areaB) return areaA - areaB

                    return calcularDistanciaBase(a) - calcularDistanciaBase(b)
                })
                posicaoFinal = candidatas[0]
            }
        }

        setPosicaoPopup(atual => {
            const mudouTop = Math.abs(atual.top - posicaoFinal.top) > 0.5
            const mudouLeft = Math.abs(atual.left - posicaoFinal.left) > 0.5

            if (!mudouTop && !mudouLeft && atual.pronto) return atual

            return {
                top: posicaoFinal.top,
                left: posicaoFinal.left,
                pronto: true,
            }
        })
    }, [aberto, dimensoes, passoAtual])

    if (!passos.length) return null

    return (
        <div
            className={cn(
                'inset-0 fixed z-[9999]',
                'bg-foreground/50 text-muted-foreground',
                'dark:bg-background/50',
                'flex opacity-0 pointer-events-none',
                'transition-opacity duration-300',
                {
                    'opacity-100 pointer-events-auto': aberto === true,
                },
            )}
            id="overlay-apresentacao"
            onClick={e => {
                const clickedEl = e.target as HTMLElement
                if (clickedEl.id === 'overlay-apresentacao') {
                    proximo()
                }
            }}
        >
            <div
                className={cn(
                    'absolute rounded-sm border border-accent',
                    'dark:border-foreground',
                    'transition-all duration-200',
                    'bg-transparent z-1',
                    {
                        'bg-accent/40 dark:bg-foreground/20':
                            !dimensoes.calculando,
                    },
                )}
                style={{
                    width: dimensoes.width,
                    height: dimensoes.height,
                    top: dimensoes.top,
                    left: dimensoes.left,
                }}
            />
            <div
                ref={popupRef}
                className={cn(
                    'flex flex-col justify-center items-center',
                    'p-4 firefox-blur-md',
                    'bg-accent/70 text-accent-foreground border boder-accent',
                    'rounded-lg absolute',
                    'w-xl h-fit max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] overflow-auto',
                    'shadow-md z-1',
                    'transition-all duration-300',
                )}
                style={{
                    top: posicaoPopup.top,
                    left: posicaoPopup.left,
                    visibility: posicaoPopup.pronto ? 'visible' : 'hidden',
                }}
            >
                {typeof passos[passoAtual]?.title === 'string' ? (
                    <span className="text-lg">{passos[passoAtual]?.title}</span>
                ) : (
                    passos[passoAtual]?.title
                )}
                {typeof passos[passoAtual]?.description === 'string' ? (
                    <p className="p-4">{passos[passoAtual]?.description}</p>
                ) : (
                    passos[passoAtual]?.description
                )}
                <div className="w-full flex flex-row items-center justify-between px-2 gap-1 *:p-2 text-sm">
                    <Button onClick={() => ignorar()} variant="outlined">
                        Ignorar
                    </Button>
                    <div className="flex flex-1 min-w-0" />
                    <Button
                        onClick={anterior}
                        variant="contained"
                        disabled={passoAtual === 0 || dimensoes.calculando}
                    >
                        Anterior
                    </Button>
                    <Button
                        onClick={proximo}
                        variant="contained"
                        disabled={dimensoes.calculando}
                    >
                        {passoAtual === passos.length - 1
                            ? 'Finalizar'
                            : 'Próximo'}
                        {` (${passoAtual + 1}/${passos.length})`}
                    </Button>
                </div>
            </div>
        </div>
    )
}

interface PersistState {
    passoAtual: number
    aberto: boolean | null
    isTudoCompleto: boolean
    abrir: () => void
    proximo: () => void
    anterior: () => void
    ignorar: () => void
}

export interface IPasso {
    getHighlightElement?: () => HTMLElement
    parentElements?: Array<() => HTMLElement>
    title: React.ReactNode
    description: React.ReactNode
}

function makeStore(name: string, passos: IPasso[]) {
    return create(
        persist<PersistState>(
            (set, get) => ({
                passoAtual: 0,
                aberto: null,
                isTudoCompleto: get()?.passoAtual > passos.length,
                abrir: () => {
                    set({ aberto: true })
                },
                proximo: () => {
                    const { passoAtual } = get()
                    if (passos.at(passoAtual + 1)) {
                        set({ passoAtual: passoAtual + 1 })
                    } else {
                        set({ aberto: false, isTudoCompleto: true })
                    }
                },
                anterior: () => {
                    const { passoAtual } = get()
                    if (passoAtual > 0) set({ passoAtual: passoAtual - 1 })
                },
                ignorar: () => {
                    set({
                        passoAtual: passos.length + 1,
                        aberto: false,
                        isTudoCompleto: true,
                    })
                },
            }),
            {
                name: `apresentacao/${name}`,
            },
        ),
    )
}
